import React, { useState } from "react";
import { Star } from "lucide-react";

// Coral (#D2673E) is the ONLY star color across the app — matches the coral
// progress bars in Rincian Penilaian and the eyebrow accents throughout.
const CORAL = "#D2673E";
const EMPTY = "#EAE7E0";

// Composite score → soft warm palette (Warm & Premium).
// Bands: >=4.5 dark-green, >=3.5 light-green, >=2.5 yellow, <2.5 soft-orange/pink (Perlu Perhatian)
export function scoreBand(value) {
  // Round to 1 decimal so the band matches what the UI actually shows (e.g. 4.48 → 4.5 → excellent).
  const v = Number(((Number(value) || 0)).toFixed(1));
  if (v >= 4.5) return { key: "excellent", label: "Sangat Direkomendasikan", bg: "#DCFCE7", border: "#86EFAC", text: "#166534", starFill: CORAL };
  if (v >= 3.5) return { key: "good",     label: "Bagus",                    bg: "#ECFDF5", border: "#A7F3D0", text: "#047857", starFill: CORAL };
  if (v >= 2.5) return { key: "fair",     label: "Cukup",                    bg: "#FEF9C3", border: "#FDE68A", text: "#854D0E", starFill: CORAL };
  return                                    { key: "needs",    label: "Perlu Perhatian",          bg: "#FEE4E2", border: "#FECDD3", text: "#9F1239", starFill: CORAL };
}

/**
 * A single star drawn by stacking an empty grey star + a colored star clipped by percentage.
 * fillPct: 0..100  ·  size: pixel size  ·  color: hex fill (also used as stroke color).
 */
function StackedStar({ fillPct, size, color, size1x = size }) {
  const clip = Math.max(0, Math.min(100, fillPct));
  return (
    <div className="relative pointer-events-none" style={{ width: size, height: size }}>
      {/* Empty base */}
      <Star size={size1x} style={{ color: EMPTY, fill: EMPTY }} className="absolute inset-0" />
      {/* Filled overlay clipped to fillPct% of the width */}
      {clip > 0 && (
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${clip}%` }}>
          <Star size={size1x} style={{ color: color, fill: color }} strokeWidth={2.2} />
        </div>
      )}
    </div>
  );
}

/** Read-only star rating that supports arbitrary fractional values 0..5.
 *  Renders using percentage-clipped fill so half-stars, 0.3-stars, etc. are all visually accurate. */
export function StarRating({ value = 0, size = 14, testid, useBandColor = false, coralColor = false }) {
  // Design rule: stars are ALWAYS coral. `useBandColor` and `coralColor` kept
  // as props for backward compatibility but no longer alter the fill.
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  const color = CORAL;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => {
        const pct = Math.max(0, Math.min(1, v - (i - 1))) * 100;
        return <StackedStar key={i} fillPct={pct} size={size} color={color} />;
      })}
    </div>
  );
}

/** Interactive picker with half-star precision (step 0.5).
 *  Clicking the LEFT half of a star sets X.5; the RIGHT half sets X.
 *  Keyboard: Arrow Left/Right adjust ±0.5. Value display "X.X / 5.0" is real-time.
 */
export function StarPicker({ value = 0, onChange, testid, size = 24 }) {
  const [hover, setHover] = useState(null); // { i, half } while pointing
  const display = hover ? (hover.i - (hover.half ? 0.5 : 0)) : Number(value) || 0;
  const change = (i, half) => onChange(i - (half ? 0.5 : 0));

  return (
    <div className="inline-flex items-center gap-2 select-none" data-testid={testid} role="slider"
         aria-valuemin={0.5} aria-valuemax={5} aria-valuenow={value}
         onKeyDown={(e) => {
           if (e.key === "ArrowLeft") { e.preventDefault(); onChange(Math.max(0.5, (Number(value) || 0) - 0.5)); }
           if (e.key === "ArrowRight") { e.preventDefault(); onChange(Math.min(5, (Number(value) || 0) + 0.5)); }
         }} tabIndex={0}>
      <div className="inline-flex items-center gap-1" onMouseLeave={() => setHover(null)}>
        {[1, 2, 3, 4, 5].map((i) => {
          const pct = Math.max(0, Math.min(1, display - (i - 1))) * 100;
          return (
            <div key={i} className="relative" style={{ width: size, height: size }}>
              <StackedStar fillPct={pct} size={size} color={CORAL} />
              {/* LEFT half → X.5 (expanded tap zone: 24px instead of 18px so
                  mobile users can hit the half-star target more reliably.
                  Overlaps the right-half by 6px on 36px stars; `z-20` ensures
                  the left button wins the click in that overlap zone.) */}
              <button type="button" tabIndex={-1} data-testid={`${testid}-half-${i}`}
                aria-label={`${i - 0.5} bintang`}
                className="absolute inset-y-0 left-0 w-6 cursor-pointer z-20"
                onMouseEnter={() => setHover({ i, half: true })}
                onClick={() => change(i, true)} />
              {/* RIGHT half → X.0 */}
              <button type="button" tabIndex={-1} data-testid={`${testid}-star-${i}`}
                aria-label={`${i} bintang`}
                className="absolute inset-y-0 right-0 w-1/2 cursor-pointer z-10"
                onMouseEnter={() => setHover({ i, half: false })}
                onClick={() => change(i, false)} />
            </div>
          );
        })}
      </div>
      <span className="text-xs text-[#4B5563] tabular-nums font-medium min-w-[54px] text-right"
            data-testid={`${testid}-value`}>
        {(Number(value) || 0) > 0
          ? <>{(Number(value) || 0).toFixed(1)} <span className="opacity-60">/ 5.0</span></>
          : <span className="text-[#8A8F99]">— / 5.0</span>}
      </span>
    </div>
  );
}
